import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import type { ReactNode } from 'react';
import { MediaCard } from '../shared/MediaCard';

export function keyList(count: number, prefix: string): string[] {
  return Array.from({ length: count }, (_, index) => `${prefix}-${index}`);
}

function TableSkeleton({ rows }: { rows: number }): ReactNode {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {keyList(rows, 'row').map((key) => (
        <Skeleton key={key} variant="rounded" height={20} />
      ))}
    </Box>
  );
}

function StatCardsSkeleton({ count }: { count: number }): ReactNode {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: `repeat(${count}, 1fr)` },
        gap: 3,
        mb: 4,
      }}
    >
      {keyList(count, 'stat').map((key) => (
        <MediaCard key={key} sx={{ p: 3 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" height={32} />
        </MediaCard>
      ))}
    </Box>
  );
}

export function ExecutiveSummarySkeleton(): ReactNode {
  return (
    <MediaCard sx={{ p: 4, mb: 4 }} data-test="executive-summary-loading">
      <Skeleton variant="text" width={200} height={24} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 4,
          mt: 2,
        }}
      >
        {keyList(3, 'summary').map((key) => (
          <TableSkeleton key={key} rows={3} />
        ))}
      </Box>
    </MediaCard>
  );
}

export function MediaBoardSkeleton(): ReactNode {
  return (
    <Box data-test="media-board-loading">
      <ExecutiveSummarySkeleton />

      <MediaCard sx={{ p: 4, mb: 4 }}>
        <Skeleton variant="text" width={220} height={24} />
        <Box sx={{ mt: 3 }}>
          <TableSkeleton rows={6} />
        </Box>
      </MediaCard>

      <MediaCard sx={{ p: 4, mb: 4 }}>
        <Skeleton variant="text" width={220} height={24} />
        <Box sx={{ mt: 3 }}>
          <TableSkeleton rows={6} />
        </Box>
      </MediaCard>

      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width={280} height={28} sx={{ mb: 3 }} />
        <StatCardsSkeleton count={3} />
        <MediaCard sx={{ p: 4 }}>
          <Skeleton variant="text" width={200} height={24} sx={{ mb: 3 }} />
          <TableSkeleton rows={5} />
        </MediaCard>
      </Box>

      <MediaCard sx={{ p: 4 }}>
        <Skeleton variant="text" width={160} height={24} />
        <Skeleton variant="text" width={100} height={40} />
        <Skeleton variant="rounded" height={180} sx={{ mt: 2 }} />
      </MediaCard>
    </Box>
  );
}
